
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, Gift, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useReferralCleanup } from "@/hooks/use-referral-cleanup";
import { CSOLoadingSpinner } from "@/components/ui/cso-loading-spinner";

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const { cleanupAfterConversion } = useReferralCleanup();
  const [isProcessing, setIsProcessing] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState<any>(null);

  useEffect(() => {
    const processPaymentSuccess = async () => {
      try {
        console.log('Processing payment success...');
        
        // Get current user first
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError || !user) {
          console.log('No user found, redirecting to auth');
          navigate('/auth');
          return;
        }

        console.log('User authenticated:', user.id);

        // Show success immediately since we're on the success URL
        toast({
          title: "Payment Successful!",
          description: "Welcome to CSO Agent! Your subscription is now active.",
          className: "bg-green-50 border-green-200",
        });

        // Check for subscription data with retries
        let subscription = null;
        let attempts = 0;
        const maxAttempts = 10;

        while (!subscription && attempts < maxAttempts) {
          const { data: subData } = await supabase
            .from('user_subscriptions')
            .select('*')
            .eq('user_id', user.id)
            .eq('status', 'active')
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          if (subData) {
            subscription = subData;
            break;
          }

          attempts++;
          if (attempts < maxAttempts) {
            console.log(`Waiting for subscription data... attempt ${attempts}/${maxAttempts}`);
            await new Promise(resolve => setTimeout(resolve, 1500));
          }
        }

        if (subscription) {
          console.log('Subscription found:', subscription);
          setSubscriptionData(subscription);
          
          // Process referral cleanup if subscription exists
          try {
            console.log('Attempting referral cleanup...');
            const result = await cleanupAfterConversion.mutateAsync({
              userId: user.id,
              subscriptionType: subscription.subscription_type
            });

            console.log('Referral cleanup result:', result);

            if (result.success && result.conversion) {
              toast({
                title: "Referral Bonus!",
                description: `Your referrer will earn $${result.commission} commission. Thank you for using a referral link!`,
                duration: 5000,
              });
            }
          } catch (referralError) {
            console.log('Referral processing failed or no referral to process:', referralError);
          }
        }

      } catch (error) {
        console.error('Error processing payment success:', error);
        toast({
          title: "Payment Processing",
          description: "Your payment was successful. It may take a moment for your subscription to activate.",
          className: "bg-blue-50 border-blue-200",
        });
      } finally {
        setIsProcessing(false);
      }
    };

    processPaymentSuccess();
  }, [searchParams, navigate, toast, cleanupAfterConversion]);

  const handleGoToDashboard = () => {
    console.log('Navigating to dashboard...');
    navigate('/dashboard', { replace: true });
  };

  if (isProcessing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <CSOLoadingSpinner size="lg" />
          <p className="mt-4 text-gray-600">Processing your payment...</p>
          <p className="mt-2 text-sm text-gray-500">This may take a few moments</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md mx-auto">
        <Card className="border-green-200 bg-green-50">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <CheckCircle className="h-16 w-16 text-green-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-green-800">
              Payment Successful!
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-6">
            <p className="text-green-700">
              Welcome to CSO Agent! Your payment has been processed successfully.
            </p>
            
            {subscriptionData && (
              <div className="bg-white p-4 rounded-lg border border-green-200">
                <div className="text-sm font-medium text-green-800 mb-2">Subscription Details:</div>
                <div className="text-sm text-green-700">
                  Plan: {subscriptionData.subscription_type.charAt(0).toUpperCase() + subscriptionData.subscription_type.slice(1)}
                  <br />
                  Status: Active
                </div>
              </div>
            )}

            {!subscriptionData && (
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4 text-orange-600" />
                  <p className="text-sm font-medium text-orange-800">Activating Subscription</p>
                </div>
                <p className="text-xs text-orange-700">Your subscription is being activated. This may take a few minutes.</p>
              </div>
            )}

            <div className="space-y-3">
              <Button 
                onClick={handleGoToDashboard}
                className="w-full bg-green-600 hover:bg-green-700 text-lg py-6 font-semibold"
                size="lg"
              >
                Go to Dashboard
              </Button>
              
              <Button 
                onClick={() => navigate('/documents')}
                variant="outline"
                className="w-full border-green-300 text-green-600 hover:bg-green-50"
              >
                Create Your First Document
              </Button>
            </div>

            <div className="mt-6 p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Gift className="h-4 w-4 text-orange-600" />
                <span className="text-sm font-medium text-orange-800">Referral Program</span>
              </div>
              <p className="text-xs text-orange-700">
                Start earning commissions by referring others to CSO Agent!
              </p>
              <Button
                onClick={() => navigate('/referrals')}
                variant="outline"
                size="sm"
                className="mt-2 w-full border-orange-300 text-orange-600 hover:bg-orange-50"
              >
                Join Referral Program
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PaymentSuccess;
